import React from 'react';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';

// Hydration-safe responsive behaviour:
// - SSR default is deterministic via theme.ssrMatchMedia (configured in theme.ts)
// - First client render is frozen to SSR result by overriding matchMedia
// - After hydration, we un-freeze by dropping the override so real matchMedia applies

const createFrozenMatchMedia = (fixedWidth: number) =>
  (query: string) => {
    // Basic parser for min/max-width queries expressed in px.
    const min = /\(\s*min-width\s*:\s*([0-9.]+)px\s*\)/.exec(query);
    const max = /\(\s*max-width\s*:\s*([0-9.]+)px\s*\)/.exec(query);
    let matches = true;
    if (min) {
      const minPx = parseFloat(min[1]);
      matches = matches && fixedWidth >= minPx;
    }
    if (max) {
      const maxPx = parseFloat(max[1]);
      matches = matches && fixedWidth <= maxPx;
    }
    // Fallback: if neither min/max present, default to false to avoid accidental mismatches.
    if (!min && !max) {
      matches = false;
    }
    // Return a stub MediaQueryList-like object sufficient for MUI useMediaQuery
    return {
      matches,
      media: query,
      onchange: null,
      addListener: () => {},
      removeListener: () => {},
      addEventListener: () => {},
      removeEventListener: () => {},
      dispatchEvent: () => false,
    } as unknown as MediaQueryList;
  };

const ResponsiveQueryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const parentTheme = useTheme();
  const [hydrated, setHydrated] = React.useState(false);

  // Consider lg as the canonical SSR viewport width. Keep in sync with theme breakpoints.
  const desktopWidth = parentTheme.breakpoints.values.lg;

  // Freeze until after hydration by overriding matchMedia to the SSR-equivalent value.
  const frozenTheme = React.useMemo(() => {
    if (hydrated) return null;
    return createTheme(parentTheme, {
      components: {
        MuiUseMediaQuery: {
          defaultProps: {
            matchMedia: createFrozenMatchMedia(desktopWidth),
          },
        },
      },
    });
  }, [hydrated, parentTheme, desktopWidth]);

  React.useEffect(() => {
    // Unfreeze on mount so queries update to real values post-hydration
    setHydrated(true);
  }, []);

  // When hydrated, render children without an extra ThemeProvider to avoid overriding behaviour
  if (hydrated || !frozenTheme) {
    return <>{children}</>;
  }

  return <ThemeProvider theme={frozenTheme}>{children}</ThemeProvider>;
};

export default ResponsiveQueryProvider;
